import React, { useMemo } from 'react';
import { useAppStore } from 'stores/appStore';
import { themeColors } from 'config/themeColors';
import styles from './ThemePreview.scss';

function rgbToHex(rgb: string): string {
  const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) {
    return rgb;
  }
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function parseRgb(rgb: string): [number, number, number] {
  const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) {
    return [255, 255, 255];
  }
  return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)];
}

function mixColors(color1: string, color2: string, ratio: number = 0.5): string {
  const [r1, g1, b1] = parseRgb(color1);
  const [r2, g2, b2] = parseRgb(color2);
  const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
  const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
  const b = Math.round(b1 * (1 - ratio) + b2 * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

function generateGradientColors(colors: string[]): string[] {
  if (colors.length !== 4) {
    return [];
  }

  const [color1, color2, color3, color4] = colors;

  return [
    mixColors(color1, color2, 0.5),
    mixColors(color2, color3, 0.5),
    mixColors(color3, color4, 0.5),
    mixColors(color1, color3, 0.5),
    mixColors(color2, color4, 0.5),
    mixColors(color1, color4, 0.5),
  ];
}

export function ThemePreview() {
  const selectedThemeKey = useAppStore((state) => state.selectedThemeKey);

  const gradientColors = useMemo(() => {
    if (!selectedThemeKey) {
      return [];
    }

    const selectedTheme = themeColors.find((theme) => theme.name === selectedThemeKey);
    if (!selectedTheme || selectedTheme.colors.length !== 4) {
      return [];
    }

    return generateGradientColors(selectedTheme.colors);
  }, [selectedThemeKey]);

  if (!selectedThemeKey || gradientColors.length === 0) {
    return (
      <div className={styles.preview}>
        <div className={styles.emptyState}>Select a theme to see color preview</div>
      </div>
    );
  }

  const leftColumnItems = [0, 2, 4];
  const rightColumnItems = [1, 3, 5];

  return (
    <div className={styles.preview}>
      <div className={styles.colorGrid}>
        <div className={styles.colorColumn}>
          {leftColumnItems.map((index) => {
            const color = gradientColors[index];
            const hexColor = rgbToHex(color);
            const darkerColor = mixColors(color, 'rgb(0, 0, 0)', 0.2);
            const darkerHex = rgbToHex(darkerColor);
            return (
              <div
                key={index}
                className={styles.colorItem}
                data-index={index}
                style={{
                  background: `linear-gradient(135deg, ${hexColor} 0%, ${darkerHex} 100%)`,
                }}
              />
            );
          })}
        </div>

        <div className={styles.colorColumn}>
          {rightColumnItems.map((index) => {
            const color = gradientColors[index];
            const hexColor = rgbToHex(color);
            const darkerColor = mixColors(color, 'rgb(0, 0, 0)', 0.2);
            const darkerHex = rgbToHex(darkerColor);
            return (
              <div
                key={index}
                className={styles.colorItem}
                data-index={index}
                style={{
                  background: `linear-gradient(135deg, ${hexColor} 0%, ${darkerHex} 100%)`,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
